import { GetStaticPaths, GetStaticPropsContext, InferGetStaticPropsType } from 'next';
// eslint-disable-next-line import/named
import { v4 as uuid } from 'uuid';

import { CHANNEL, accountPriceListPagesSlugs, overviewPageSlugs } from '@boss/constants/b2b-b2c';
import { contentfulContext } from '@boss/contentful-client';
import { getSlugMap } from '@boss/contentful-client/b2b-b2c';
import { useRouter } from '@boss/hooks';
import { content as contentService } from '@boss/services';
import type { Environment, LOCALE_CODE } from '@boss/types/b2b-b2c';
import { Presence } from '@boss/ui';
import { getLastPartOfPath, isArrayWithContent } from '@boss/utils';

// FIXME: This is a temporary solution to ignore the enforce-module-boundaries rule that prevents circular dependencies
// eslint-disable-next-line @nx/enforce-module-boundaries
import { defaultLocale } from '../../../config/locales.config';
import { ComponentMapper, LoginSection, SEO } from '../components';
import { IS_BUILD, PageTypes } from '../constants';
import { usePermissionGuard, useProfile } from '../hooks';
import Layout from '../layout/default';
import { getRedirectDestinationSlugs, homePageConfig, overviewPageTypes } from '../utils';
import { getAdditionalPageData } from '../utils/cms';
import getServerSideTranslations from '../utils/getServerSideTranslations';
import {
  mapContentfulToNextLocale,
  mapNextToContentfulLocaleMarket,
  mapNextToContentfulMarket,
} from '../utils/localeMapper';
import { getMappedNavigation } from '../utils/navigation';

export const config = {
  maxDuration: 30,
};

const checkOverviewPageTypeExists = (overviewPageSlug: string | undefined, locale: string): boolean => {
  return (
    !!overviewPageSlug &&
    overviewPageTypes.some(
      type => overviewPageSlugs[type as keyof typeof overviewPageSlugs]?.[locale] === overviewPageSlug,
    )
  );
};

const constructOverviewPage = (
  overviewPageSlug: string | undefined,
  locale: string,
): {
  slug: string;
  type: string;
} | null => {
  if (!overviewPageSlug) {
    return null;
  }
  const type = overviewPageTypes.find(
    type => overviewPageSlugs[type as keyof typeof overviewPageSlugs]?.[locale] === overviewPageSlug,
  );

  return type ? { slug: overviewPageSlug, type } : null;
};

// TODO: This template could collide with other templates used.
// In this method there should be a check to check for forbidden patterns
// These should also be marked as reserved paths in contentful as well
export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const slugMap = await getSlugMap({
    preview: false,
    channel: CHANNEL,
    contentType: 'contentPage',
  });

  const getPageOverviewPrefix = (locale: string, tags: string[]) => {
    if (tags?.length && overviewPageTypes.some(type => tags.includes(type))) {
      const overviewPageType = overviewPageTypes.find(type => tags.includes(type)) as keyof typeof overviewPageSlugs;

      if (overviewPageType && overviewPageSlugs[overviewPageType]) {
        return overviewPageSlugs[overviewPageType][locale];
      }
    }

    return '';
  };

  const pages = slugMap
    // TODO: Find a more scaleble way to lock this scenario
    .filter(
      ({ slug, locale }) =>
        !['home', 'stores', 'roadmap', 'p', 'c', 'api', 'inspiratiebeelden', '404'].includes(slug) &&
        locales?.includes(mapContentfulToNextLocale(locale)),
    )
    .map(({ slug, locale, tags }) => {
      const nextLocale = mapContentfulToNextLocale(locale);
      const pagePrefix = tags?.length ? getPageOverviewPrefix(nextLocale, tags) : undefined;

      /***
       * this can stay the same-ish except the locale is not a contentful locale, but rather constructed from other props
       * */
      return {
        params: {
          slug: pagePrefix ? [pagePrefix, slug] : [slug],
        },
        locale: nextLocale,
      };
    });

  return {
    paths: pages,
    /* 
      We need to set fallback to blocking, so that new pages can be previewed. 
      The 404 handling will take place in the getStaticProps function.
    */
    fallback: 'blocking',
  };
};

const isValidPagePath = (
  slug: string,
  pagePathLength: number,
  overviewPageSlug: string | undefined,
  locale: string | undefined,
  contentfulLocaleMarket: string | null,
  market: 'BE' | 'NL',
): boolean => {
  if (!slug || slug === 'home' || pagePathLength > 2) {
    return false;
  }
  if (
    pagePathLength === 2 &&
    (!overviewPageSlug || !locale || !checkOverviewPageTypeExists(overviewPageSlug, locale))
  ) {
    return false;
  }
  return !!contentfulLocaleMarket && !!market;
};

export const getStaticProps = async ({
  preview,
  params,
  locale,
  locales,
}: GetStaticPropsContext<{ slug: string; locale: LOCALE_CODE }>) => {
  if (!locale) {
    return {
      notFound: true,
    };
  }

  const _locale = locale === 'default' ? defaultLocale : locale;
  const contentfulLocaleMarket = mapNextToContentfulLocaleMarket(_locale);
  const market = mapNextToContentfulMarket(_locale);

  const pagePathLength = params?.slug?.length ?? 0;
  const slug = params?.slug[pagePathLength - 1];
  const overviewPageSlug = pagePathLength === 2 ? params?.slug[0] : undefined;

  if (!slug || !isValidPagePath(slug, pagePathLength, overviewPageSlug, locale, contentfulLocaleMarket, market)) {
    return {
      notFound: true,
    };
  }

  // SET GLOBAL MARKET CONTEXT
  contentfulContext.market = market;

  /**
   * simply change locale here
   * */
  const [pageData, navigation] = await Promise.all([
    contentService.getContentPageBySlug({
      preview: !!preview,
      slug: getLastPartOfPath(slug),
      locale: contentfulLocaleMarket as LOCALE_CODE,
      channel: CHANNEL,
      market,
      defaultLocale,
      defaultContentfulLocale: mapNextToContentfulLocaleMarket(defaultLocale),
      allLocales: locales
        ?.filter(locale => locale !== 'default')
        .map(locale => mapNextToContentfulLocaleMarket(locale)),
      batchMode: IS_BUILD,
    }),
    getMappedNavigation(contentfulLocaleMarket, !!preview),
  ]);

  const overviewPage = constructOverviewPage(overviewPageSlug, locale);

  const isOverviewPage = pageData?.tags?.some(tag => overviewPageTypes.includes(tag as keyof typeof overviewPageSlugs));

  /*
   * todo investigate this change
   * */
  if (
    !pageData ||
    !pageData?.publishForLocales?.includes(contentfulLocaleMarket as LOCALE_CODE) ||
    (overviewPage?.type && !pageData?.tags?.includes(overviewPage?.type)) ||
    (isOverviewPage && !overviewPageSlug)
  ) {
    return {
      notFound: true,
    };
  }

  const additionalData = await getAdditionalPageData(pageData.content, contentfulLocaleMarket, CHANNEL, IS_BUILD);

  return {
    props: {
      pageType: isOverviewPage ? PageTypes.OVERVIEW_PAGE : PageTypes.CONTENT,
      preview: !!preview,
      locale: _locale,
      overviewPage,
      slug,
      pageData: pageData ?? {},
      additionalData: additionalData ?? {},
      environment: process.env.CONTENTFUL_ENVIRONMENT as Environment,
      navigation,
      ...(await getServerSideTranslations(_locale, ['common', 'stores', 'routes', 'forms'])),
    },
  };
};

export const Index = ({
  pageData,
  environment,
  preview,
  slug,
  navigation,
  locale,
  additionalData,
  overviewPage,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { showLoginSection, redirect: homePageRedirect } = homePageConfig;
  const { asPath, push } = useRouter();
  const { isLoggedIn } = useProfile();
  const isPriceListPage = accountPriceListPagesSlugs[locale] === `/${slug}`;
  // [45935] The acties page is a Contentful contentPage served by this
  // catch-all; its slug is uniform across locales. showprice = false blocks it
  // regardless of canorder — the same 'showprice' requirement Facturen and
  // Prijslijsten use, silently redirecting to the accountcenter.
  const isActiesPage = slug === 'acties';

  const { pending, blocked } = usePermissionGuard(isPriceListPage || isActiesPage ? 'showprice' : undefined);
  const crumbles = [{ href: slug, label: pageData.pageTitle }]?.filter(v => !!v.href); // Exclude crumbles without href like the homepage

  if (overviewPage) {
    crumbles.unshift({
      href: overviewPage?.slug,
      label: overviewPage?.slug,
    });
  }

  const mapTranslatedSlugs = (translatedSlugs: Record<string, string>) => {
    return Object.keys(translatedSlugs).reduce((acc, key) => {
      const locale = mapContentfulToNextLocale(key);
      const slug = translatedSlugs[key];

      if (overviewPage) {
        const translatedOverviewPage = overviewPageSlugs[overviewPage.type as keyof typeof overviewPageSlugs][locale];

        acc[locale] = `${translatedOverviewPage}${slug.startsWith('/') ? slug : '/' + slug}`;
      } else {
        acc[locale] = slug;
      }

      return acc;
    }, {} as Record<string, string>);
  };

  const localizedSlugs = mapTranslatedSlugs(pageData.translatedSlugs);

  const isHomePage = asPath === '/';
  const redirectDestination = isHomePage && getRedirectDestinationSlugs(homePageRedirect?.destination);

  if (!!homePageRedirect?.auth === isLoggedIn && redirectDestination) {
    push(redirectDestination[locale]);
  }

  // [39787] Render gate: never paint restricted price-list content. Non-guarded
  // pages have requirement undefined → pending/blocked stay false.
  if (pending || blocked) {
    return null;
  }

  return (
    <SEO
      breadcrumbs={crumbles}
      description={pageData.metaDescription}
      imageSrc={pageData.ogImage?.image?.file?.url}
      localizedSlugs={localizedSlugs}
      navigation={navigation}
      noFollow={pageData.noFollow}
      noIndex={pageData.noIndex}
      title={pageData.pageTitle}
    >
      <Layout
        crumbles={crumbles}
        environment={environment}
        localizedSlugs={localizedSlugs}
        navigation={navigation}
        preview={preview}
      >
        <Presence id="login-section-presence" visible={isHomePage && showLoginSection}>
          <LoginSection visible="anonymous" />
        </Presence>

        {isArrayWithContent(pageData?.content) &&
          pageData.content.map(entry => (
            <ComponentMapper
              colors={additionalData.colors}
              entry={entry}
              key={uuid()}
              locale={locale}
              products={additionalData.products}
              theme={pageData.theme}
            />
          ))}
      </Layout>
    </SEO>
  );
};

export default Index;
